import { useMemo } from 'react'

import type { UseQueryOptions } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'

import type {
  DocumentsApiGetDocumentsRequest as GetDocumentsParams,
  DocumentsOutput,
  FullUpdateDto as CreateDocumentParams,
  DocumentProvidedBy,
} from '@b-stock/location-api-client'

import docservApi from '@api/docserv'
import documentApi from '@api/document'
import useUserInfo from '@helpers/useUserInfo'

const baseKey = 'documents'

export const useDocuments = (
  filters?: GetDocumentsParams,
  opts?: Omit<
    UseQueryOptions<unknown, unknown, DocumentsOutput | undefined, any>,
    'queryKey' | 'queryFn'
  >
) => {
  const { accountId } = useUserInfo()
  const queryKey = [baseKey, accountId, filters]

  const query = useQuery({
    queryKey: queryKey,

    queryFn: async () => {
      const allDocuments: DocumentsOutput['documents'] = []
      const pageLimit = 100

      let total = 0
      let tempOffset = 0
      let lastResp = null

      do {
        const resp = await documentApi.getDocuments({
          offset: tempOffset,
          limit: pageLimit,
          ...filters,
        })
        lastResp = resp

        allDocuments.push(...resp.documents)
        total = resp.total
        tempOffset += pageLimit
      } while (tempOffset < total)

      return {
        ...lastResp,
        documents: allDocuments,
      }
    },

    refetchOnMount: 'always',
    ...opts,
    enabled: !!accountId && opts?.enabled !== false,
    placeholderData: { total: 0, limit: 0, offset: 0, documents: [] },
  })
  return {
    ...query,
    isLoading: query.isLoading || query.isRefetching,
  }
}

export const useDocs = (
  params: GetDocumentsParams,
  opts?: Omit<
    UseQueryOptions<unknown, unknown, DocumentsOutput, any[]>,
    'queryKey' | 'queryFn'
  >
) => {
  const { accountId } = useUserInfo()
  const queryKey = useMemo(
    () => ['DocumentsApiGetDocumentsRequest', accountId, params],
    [accountId, params]
  )

  return useQuery({
    queryKey: queryKey,

    queryFn: async (): Promise<DocumentsOutput> => {
      if (!accountId) {
        throw new Error(
          'User does not appear to be logged in. Can not request docs'
        )
      }

      const allDocuments: DocumentsOutput['documents'] = []
      const pageLimit = 100 // to test this cycle make pageLimit 3

      let total = 0
      let tempOffset = 0
      let lastResp = null

      do {
        const resp = await documentApi.getDocuments({
          accountId: [accountId],
          offset: tempOffset,
          limit: pageLimit,
          ...params,
        })
        lastResp = resp

        allDocuments.push(...resp.documents)
        total = resp.total
        tempOffset += pageLimit
      } while (tempOffset < total)

      return {
        ...lastResp,
        documents: allDocuments,
      }
    },
    ...opts,
  })
}

type UploadBusinessDocumentParams = CreateDocumentParams & {
  accountId: string
  file?: Blob | string
  providedBy: DocumentProvidedBy
}

export const uploadBusinessDocument = async (
  data: UploadBusinessDocumentParams
) => {
  const { file, providedBy, ...payload } = data

  if (file && typeof file !== 'string') {
    const response = await docservApi.create(file)

    if (!response.data) {
      throw new Error('Did not receive an expected response from docserv')
    }

    payload.document = {
      link: {
        docservId: response.data.id,
        mimeType: response.data.contentType,
      },
      providedBy,
    }
  }

  return documentApi.create(payload)
}
